import { CheckboxWithLabel } from '@adotel/components'
import { useApolloClient, useMutation } from '@apollo/client'
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Typography
} from '@material-ui/core'
import { useState } from 'react'
import { toast } from 'react-toastify'

import SectionTitle from '../../components/SectionTitle'
import * as notificationGraphql from '../../graphql/notifications'
import { useCursorPage } from '../../helpers/useCursorPage'
import { NotificationListSkeleton } from './NotificationListSkeleton'
import { formatNotificationTime } from './notificationAccess'
import {
  EmptyState,
  ErrorState,
  LoadMoreButton,
  RETAINED_RESULTS,
  t
} from './shared'
import useStyles from './styles/InboxPanel.styles'
import { InboxItem } from './types'

export function InboxPanel() {
  const classes = useStyles()
  const client = useApolloClient()
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [locallyRead, setLocallyRead] = useState<Record<string, boolean>>({})
  const { items, loading, error, hasMore, loadMore, refetch } = useCursorPage<
    Record<string, any>,
    InboxItem
  >({
    query: notificationGraphql.NOTIFICATION_INBOX_QUERY,
    variables: { unreadOnly },
    maxRetained: RETAINED_RESULTS,
    getPage: (data) =>
      data && {
        items: data.notificationInbox?.notifications ?? [],
        nextPageToken: data.notificationInbox?.nextPageToken
      },
    withItems: (data, notifications) => ({
      ...data,
      notificationInbox: { ...data.notificationInbox, notifications }
    })
  })
  const [markRead, { loading: markingRead }] = useMutation(
    notificationGraphql.MARK_NOTIFICATIONS_READ_MUTATION
  )
  const [markAll, { loading: markingAll }] = useMutation(
    notificationGraphql.MARK_ALL_NOTIFICATIONS_READ_MUTATION
  )
  const [archive, { loading: archiving }] = useMutation(
    notificationGraphql.ARCHIVE_NOTIFICATIONS_MUTATION
  )
  const setUnreadCount = (update: (current: number) => number) =>
    client.cache.modify({
      fields: {
        notificationUnreadCount(existing = 0) {
          return update(existing)
        }
      }
    })

  if (loading && !items.length)
    return <NotificationListSkeleton label={t('Loading inbox')} />
  if (error)
    return (
      <ErrorState
        message="Unable to load notification inbox."
        onRetry={() => refetch()}
      />
    )

  return (
    <Box>
      <SectionTitle title={t('Inbox')} />
      <Box
        display="flex"
        alignItems="center"
        className={classes.actionRow}
        mb={2}>
        <FormControlLabel
          control={
            <CheckboxWithLabel
              checked={unreadOnly}
              noHelperText
              onChange={(event) => setUnreadOnly(event.currentTarget.checked)}
            />
          }
          label={t('Unread only')}
        />
        <Button
          variant="outlined"
          disabled={markingAll}
          onClick={async () => {
            try {
              await markAll()
              setUnreadCount(() => 0)
              setLocallyRead({})
              await refetch()
            } catch (actionError) {
              toast.error(t('Unable to mark notifications read.'))
            }
          }}>
          {markingAll ? t('Marking…') : t('Mark all read')}
        </Button>
      </Box>
      {items.length === 0 ? (
        <EmptyState label="Notifications" />
      ) : (
        items.map((item) => (
          <Paper
            key={item.id ?? `${item.categoryKey}-${item.occurredAt}`}
            variant="outlined"
            className={classes.inboxCard}>
            <Typography variant="h6">{item.title}</Typography>
            <Typography color="textSecondary">
              {item.categoryKey} · {item.severity}
            </Typography>
            <Typography>{item.body}</Typography>
            <Typography color="textSecondary">
              {formatNotificationTime(item.occurredAt)}
            </Typography>
            <Box mt={1} display="flex" className={classes.actionRow}>
              {!item.readAt && !locallyRead[item.id] && (
                <Button
                  size="small"
                  disabled={markingRead}
                  onClick={async () => {
                    try {
                      await markRead({ variables: { ids: [item.id] } })
                      setUnreadCount((current) => Math.max(0, current - 1))
                      setLocallyRead((current) => ({
                        ...Object.fromEntries(
                          Object.entries(current).slice(-99)
                        ),
                        [item.id]: true
                      }))
                    } catch (actionError) {
                      toast.error(t('Unable to mark notification read.'))
                    }
                  }}>
                  {markingRead ? t('Marking…') : t('Mark read')}
                </Button>
              )}
              <Button
                size="small"
                disabled={archiving}
                onClick={async () => {
                  try {
                    await archive({ variables: { ids: [item.id] } })
                    if (!item.readAt && !locallyRead[item.id]) {
                      setUnreadCount((current) => Math.max(0, current - 1))
                    }
                    await refetch()
                  } catch (actionError) {
                    toast.error(t('Unable to archive notification.'))
                  }
                }}>
                {archiving ? t('Archiving…') : t('Archive')}
              </Button>
            </Box>
          </Paper>
        ))
      )}
      {hasMore && <LoadMoreButton onLoadMore={async () => loadMore()} />}
    </Box>
  )
}
